import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { workflowsAPI } from '../../../lib/workflowsApi';
import type { ConfirmWebSearchPayload, TriggerWebSearchPayload } from '../types';

export function useTriggerWebSearch() {
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({
      existingEmail: _e,
      existingPhone: _p,
      ...apiPayload
    }: TriggerWebSearchPayload & { existingEmail?: string; existingPhone?: string }) =>
      workflowsAPI.triggerWebSearch(apiPayload).then((r) => r.data),
    onSuccess: (_data, payload) => {
      trackEvent(mp, 'web_search.triggered', {
        lead_has_email: !!payload.existingEmail,
        lead_has_phone: !!payload.existingPhone,
      });
    },
  });
}

export function useConfirmWebSearch() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (payload: ConfirmWebSearchPayload) =>
      workflowsAPI.confirmWebSearch(payload).then((r) => r.data),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', payload.leadId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      trackEvent(mp, 'web_search.confirmed', {
        contacts_saved: payload.contacts.length,
        email_saved: payload.contacts.some((c) => c.type === 'email'),
        phone_saved: payload.contacts.some((c) => c.type === 'phone'),
      });
    },
  });
}

export function useCancelWebSearch() {
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ leadId, organizationId }: { leadId: string; organizationId: string }) =>
      workflowsAPI.cancelWebSearch(leadId, organizationId).then((r) => r.data),
    onSuccess: () => {
      trackEvent(mp, 'web_search.cancelled');
    },
  });
}

export function useWebSearchResults(leadId: string, organizationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['web-search-results', leadId],
    queryFn: () => workflowsAPI.getWebSearchResults(leadId, organizationId).then((r) => r.data),
    enabled,
  });
}
